import { statusOf } from "@/data/bloodReport";
import { ArrowUp, ArrowDown, Minus } from "lucide-react";

const fmt = (v) => (Number.isInteger(v) ? v : parseFloat(v.toFixed(2)));

const FLAG = {
  high: { label: "HIGH ▲", cls: "border-red-300 bg-red-50 text-red-600" },
  low: { label: "LOW ▼", cls: "border-amber-300 bg-amber-50 text-amber-600" },
  normal: { label: "NORMAL", cls: "border-slate-200 bg-slate-50 text-slate-400" },
};

// How far a value sits outside its reference interval (0 if normal).
const deviation = (a, x) => (x < a.low ? a.low - x : x > a.high ? x - a.high : 0);

// Trend of current value vs the prior visit, and whether it's improving/worsening.
const trendOf = (a) => {
  if (a.prev == null) return null;
  const dir = a.value > a.prev ? "up" : a.value < a.prev ? "down" : "flat";
  const cur = deviation(a, a.value);
  const old = deviation(a, a.prev);
  return { dir, worsening: cur > old, improving: cur < old };
};

// ── Organ-system mapping ──────────────────────────────────────────────
// Small inline SVG organ icons (lucide-style stroke) for the systems strip.
const OrganIcon = ({ type, className }) => {
  const p = { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round", className };
  if (type === "renal") return <svg {...p}><path d="M8 4C5 4 3 7.5 3 12s2 8 5 8c2.2 0 3-1.8 3-3.6 0-2-1.8-2.4-1.8-4.4S11 8 11 6c0-1.3-.8-2-3-2z" /><path d="M16 4c3 0 5 3.5 5 8s-2 8-5 8c-2.2 0-3-1.8-3-3.6 0-2 1.8-2.4 1.8-4.4S13 8 13 6c0-1.3.8-2 3-2z" /></svg>;
  if (type === "hepatic") return <svg {...p}><path d="M3 7c0-1 .8-2 2-2h12c2.7 0 4 1.6 4 4 0 4.4-3.3 7.5-8.2 7.5-1.6 0-2.6.9-3.8.9C6 17.3 3 13.4 3 7z" /><path d="M9 9c1.5 1 3.5 1 5 0" /></svg>;
  if (type === "marrow") return <svg {...p}><circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="3" /></svg>;
  return <svg {...p}><path d="M12 3c2.2 3 5 4.6 5 8.6a5 5 0 0 1-10 0c0-2 1-3.2 1.6-4.2.8 1.1 1.6 1.6 2.6 1.6C11 8.4 10 5.6 12 3z" /></svg>;
};

const SYSTEMS = [
  { key: "renal", label: "Kidney", analytes: ["Urea", "Creatinine", "Phosphate", "Potassium"], active: "border-amber-300 bg-amber-50 text-amber-700" },
  { key: "hepatic", label: "Liver", analytes: ["ALT", "ALP", "Albumin", "Glucose", "Total bilirubin"], active: "border-orange-300 bg-orange-50 text-orange-700" },
  { key: "marrow", label: "Red cells / marrow", analytes: ["HCT (PCV)", "Reticulocytes"], active: "border-rose-300 bg-rose-50 text-rose-700" },
  { key: "inflam", label: "Inflammation", analytes: ["WBC", "Neutrophils", "Globulin"], active: "border-indigo-300 bg-indigo-50 text-indigo-700" },
];

// A strip of organ-system chips that light up once the abnormal pattern is revealed.
function OrganSystemStrip({ panel, revealed }) {
  const involved = (sys) => panel.some((a) => sys.analytes.includes(a.analyte) && statusOf(a) !== "normal");
  return (
    <div className="mb-3" data-testid="organ-strip">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1.5">Organ systems involved</div>
      <div className="flex flex-wrap gap-2">
        {SYSTEMS.map((sys) => {
          const active = revealed && involved(sys);
          return (
            <div
              key={sys.key}
              data-testid={`organ-${sys.key}`}
              data-active={active ? "true" : "false"}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-semibold transition-colors duration-500 ${active ? sys.active + " shadow-sm" : "border-slate-200 bg-slate-50 text-slate-300"}`}
            >
              <OrganIcon type={sys.key} className="w-4 h-4" />
              <span>{sys.label}</span>
            </div>
          );
        })}
      </div>
      {!revealed && <div className="text-[10px] text-slate-400 mt-1.5">Answer to see which systems the pattern points to.</div>}
    </div>
  );
}

// Small "vs prior" trend tag shown next to the current value.
function TrendTag({ a, i }) {
  const t = trendOf(a);
  if (!t) return null;
  const cls = t.worsening ? "text-red-500" : t.improving ? "text-emerald-600" : "text-slate-400";
  const Arrow = t.dir === "up" ? ArrowUp : t.dir === "down" ? ArrowDown : Minus;
  return (
    <div className={`flex items-center gap-0.5 text-[10px] font-medium mt-0.5 ${cls}`} data-testid={`analyte-trend-${i}`}>
      <Arrow className="w-3 h-3" />
      <span className="text-slate-400 font-normal">prior</span>
      <span className="font-mono-clinical">{fmt(a.prev)}</span>
    </div>
  );
}

// Horizontal reference-range bar with a marker for the value (+ faint prior marker).
function RangeBar({ a }) {
  const span = a.high - a.low || a.high * 0.2 || 1;
  let dispMin = a.low - span * 0.6;
  if (a.low >= 0 && dispMin < 0) dispMin = 0;
  const dispMax = a.high + span * 0.6;
  const range = dispMax - dispMin || 1;
  const pct = (x) => Math.max(2, Math.min(98, ((x - dispMin) / range) * 100));
  const st = statusOf(a);
  const markColor = st === "normal" ? "#0f766e" : "#dc2626";

  return (
    <div className="relative h-6">
      <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0 h-1.5 rounded-full bg-slate-200" />
      <div className="absolute top-1/2 -translate-y-1/2 h-1.5 rounded-full bg-emerald-300"
        style={{ left: `${pct(a.low)}%`, width: `${pct(a.high) - pct(a.low)}%` }} />
      {a.prev != null && (
        <div className="absolute top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full border-2 border-slate-400 bg-white opacity-70"
          style={{ left: `${pct(a.prev)}%`, transform: "translate(-50%, -50%)" }} title={`Prior: ${fmt(a.prev)}`} />
      )}
      <div className="absolute top-1/2 -translate-y-1/2 w-3.5 h-3.5 rounded-full border-2 border-white shadow"
        style={{ left: `${pct(a.value)}%`, transform: "translate(-50%, -50%)", backgroundColor: markColor }} />
    </div>
  );
}

/**
 * Read-only graphical lab report. Out-of-range results are flagged (HIGH/LOW)
 * and shown in red so the learner reads the pattern. Where a prior result
 * exists, a faint marker + trend tag give a longitudinal view. An organ-system
 * strip lights up once `revealed` is true (after the learner answers).
 */
export function LabReportPanel({ panel, revealed }) {
  const groups = [...new Set(panel.map((a) => a.group))];
  const hasPrev = panel.some((a) => a.prev != null);
  return (
    <div data-testid="lab-report-wrap">
      <OrganSystemStrip panel={panel} revealed={revealed} />
      <div className="rounded-md border border-slate-300 bg-white overflow-hidden" data-testid="lab-report">
        <div className="bg-slate-800 text-white px-3.5 py-2 text-xs font-semibold font-mono-clinical tracking-wide flex justify-between">
          <span>IN-HOUSE BLOOD ANALYSER · RESULTS</span>
          <span className="text-slate-400">{hasPrev ? "RESULT vs PRIOR" : "REF INTERVAL"}</span>
        </div>
        {groups.map((g) => (
          <div key={g}>
            <div className="px-3.5 py-1.5 bg-slate-100 text-[10px] uppercase tracking-widest text-slate-500 font-bold">{g}</div>
            {panel.map((a, i) => {
              if (a.group !== g) return null;
              const st = statusOf(a);
              const flag = FLAG[st];
              const teach = st === "high" ? a.teachHigh : st === "low" ? a.teachLow : null;
              return (
                <div key={i} className="px-3.5 py-2.5 border-t border-slate-100" data-testid={`analyte-${i}`}>
                  <div className="grid grid-cols-12 gap-2 items-center">
                    <div className="col-span-12 sm:col-span-3">
                      <div className="text-sm font-semibold text-slate-800">{a.analyte}</div>
                      <div className="text-[11px] text-slate-400 font-mono-clinical">{a.low}–{a.high} {a.unit}</div>
                    </div>
                    <div className="col-span-5 sm:col-span-2">
                      <span className={`text-base font-bold font-mono-clinical ${st === "normal" ? "text-slate-800" : "text-red-600"}`} data-testid={`analyte-value-${i}`}>
                        {fmt(a.value)}
                      </span>
                      <span className="text-[11px] text-slate-400 ml-1">{a.unit}</span>
                      <TrendTag a={a} i={i} />
                    </div>
                    <div className="col-span-7 sm:col-span-4">
                      <RangeBar a={a} />
                    </div>
                    <div className="col-span-12 sm:col-span-3 flex sm:justify-end">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded border ${flag.cls}`} data-testid={`analyte-flag-${i}`}>{flag.label}</span>
                    </div>
                  </div>
                  {revealed && teach && (
                    <div className="mt-2 text-[11px] text-slate-600 leading-snug" data-testid={`analyte-feedback-${i}`}>
                      <span className="font-semibold text-slate-800">{a.analyte} {st === "high" ? "high" : "low"}:</span> {teach}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
